/**
 * @module components/analysis/HitPotentialSection
 * @description
 *   Music-only "Hit Potential & Market Analysis" card.
 *
 *   Layout (top → bottom):
 *   1. Hit potential score (paywalled)
 *   2. Trend alignment summary (Genre fit + Audience cards with View) + panels
 *   3. Trend insights (async LLM tips) + Trending features chips
 *
 *   Genre fit focus skim:
 *   - Tip taps / Axis fit row clicks set focusAxisId (row highlight + radar dots)
 *   - Click-away from axis rows clears focus without closing View
 *   - Card View opens Genre Fit panel; Minimize closes (no Hide toggle)
 *
 *   Trend insights async / safety skim:
 *   - Live upload: pending + empty notes + tipJobId + tipsFallbackNotes (hidden)
 *   - Poll GET /api/trend-insights/:tipJobId while skeletons show
 *   - Ready poll → LLM/template notes; badges + View axis jump unchanged
 *   - 400/401/403/404 or max wait → tipsFallbackNotes (never empty "ready")
 *   - showPremiumContent gates polling and the unlocked tips column
 *   - Free-tier upload JSON is server-redacted (no tipJobId / demos / hit);
 *     locked UI uses placeholders and must tolerate null demographics / hit
 *
 *   Genre / demographics honesty:
 *   - Trend alignment Genre row uses the subgenre when set (Trap), else parent.
 *     Card eyebrows stay short (“Genre fit” / “Audience”) — lane is not repeated.
 *     View opens full-width panels; Audience panel title still stamps the lane.
 *   - Genre Fit View / radar still name the pack actually scored
 *     (Parent · Sub on the radar when a child pack is center)
 *
 * @extracted-from client/src/App.js (lines ~2383–2664, Phase 7)
 * @see server/services/analysis/trendInsightGenerator.js
 * @see docs/GENRE_PACK_RESEARCH.md
 * @see docs/SUBGENRE_AUDIENCE_RESEARCH.md
 */

import React, { useEffect, useState } from 'react';
import { TrendingUp } from 'lucide-react';
import { getHitPotentialColor, getHitPotentialBadgeTone, formatAgeGroup, formatMarket } from '../../utils/scoreHelpers';
import { Card, SectionHeader, LockOverlay, IconTile, Badge, Chip } from '../ui';
import GenreFitLearnMore from './GenreFitLearnMore';
import AudienceLearnMore from './AudienceLearnMore';
import { getAxisFitLabel } from './genreFitDefinitions';
import api from '../../api/client';
import { formatMusicGenreDisplay, formatParentGenreLabel } from '../../constants/musicGenreLabels';

const GENRE_FIT_PANEL_MS = 420;
/** Snappier than Genre Fit — Audience panel open/close. */
const AUDIENCE_PANEL_MS = 240;
/** Poll interval while tipsStatus is pending. */
const TIP_POLL_MS = 1500;
/** Stop polling after this window; fall back to tipsFallbackNotes. */
const TIP_POLL_MAX_MS = 30000;

/** Accept object tips or legacy plain strings from older sessions. */
function normalizeInsightNotes(rawNotes) {
  return (rawNotes || []).slice(0, 3).map((note) =>
    typeof note === 'string' ? { kind: 'move', text: note } : note
  );
}

function pollErrorStatus(error) {
  return error?.response?.status ?? error?.status ?? null;
}

/** Parent · Subgenre when a subgenre was selected; else parent only. */
function formatSelectedGenreDisplay(trendAnalysis) {
  const breakdown = trendAnalysis?.trendFitBreakdown;
  return formatMusicGenreDisplay(
    breakdown?.selectedGenre || trendAnalysis?.primaryGenre,
    breakdown?.selectedSubgenre || trendAnalysis?.primarySubgenre
  );
}

/**
 * Scored-lane caption: subgenre label when set, else parent.
 * Trap → "Trap"; plain Hip Hop → "Hip Hop".
 */
function formatScoredLaneLabel(trendAnalysis) {
  const breakdown = trendAnalysis?.trendFitBreakdown;
  if (breakdown?.selectedSubgenreLabel) return breakdown.selectedSubgenreLabel;
  const parent = breakdown?.selectedGenre || trendAnalysis?.primaryGenre;
  const sub = breakdown?.selectedSubgenre || trendAnalysis?.primarySubgenre;
  if (sub) {
    const combined = formatMusicGenreDisplay(parent, sub);
    const parentLabel = formatParentGenreLabel(parent);
    if (parentLabel && combined.startsWith(`${parentLabel} · `)) {
      return combined.slice(parentLabel.length + 3);
    }
  }
  return breakdown?.selectedGenreLabel || formatParentGenreLabel(parent) || '—';
}

/**
 * @param {object} props
 * @param {{ trendAnalysis: object }} props.results
 * @param {'music'|'book'} props.craftType
 * @param {boolean} props.showPremiumContent
 */
export default function HitPotentialSection({ results, craftType, showPremiumContent }) {
  // Genre fit View open/close + axis focus (from tip taps).
  const [showGenreFitLearnMore, setShowGenreFitLearnMore] = useState(false);
  const [focusAxisId, setFocusAxisId] = useState(null);
  const [genreFitPanelVisible, setGenreFitPanelVisible] = useState(false);
  const [genreFitPanelEntered, setGenreFitPanelEntered] = useState(false);
  const [showAudienceLearnMore, setShowAudienceLearnMore] = useState(false);
  const [audiencePanelVisible, setAudiencePanelVisible] = useState(false);
  const [audiencePanelEntered, setAudiencePanelEntered] = useState(false);
  const [sectionCollapsed, setSectionCollapsed] = useState(false);

  // --- Tip poll state --------------------------------------------------------
  // tipJobId + tipsFallbackNotes from live upload JSON.
  // liveNotes stays empty while pending (skeletons); poll or failover fills it.
  const tipJobId = results?.trendAnalysis?.tipJobId || null;
  const tipsFallbackNotes = normalizeInsightNotes(
    results?.trendAnalysis?.tipsFallbackNotes
  );
  const initialTipsStatus = results?.trendAnalysis?.tipsStatus || null;
  const [tipsStatus, setTipsStatus] = useState(initialTipsStatus);
  const [liveNotes, setLiveNotes] = useState(() =>
    normalizeInsightNotes(results?.trendAnalysis?.alignmentNotes)
  );
  const [tipsSource, setTipsSource] = useState(results?.trendAnalysis?.tipsSource || null);

  const openGenreFitLearnMore = (axisId = null) => {
    setFocusAxisId(axisId || null);
    setShowGenreFitLearnMore(true);
  };

  const closeGenreFitLearnMore = () => {
    setShowGenreFitLearnMore(false);
    setFocusAxisId(null);
  };

  /** Fail closed: show upload templates and stop skeletons. */
  const applyFallbackTips = (source = 'template') => {
    setTipsStatus('ready');
    setTipsSource(source);
    setLiveNotes(tipsFallbackNotes);
  };

  // Keep local tip state in sync when a new analysis result is loaded.
  useEffect(() => {
    setTipsStatus(results?.trendAnalysis?.tipsStatus || null);
    setLiveNotes(normalizeInsightNotes(results?.trendAnalysis?.alignmentNotes));
    setTipsSource(results?.trendAnalysis?.tipsSource || null);
  }, [
    results?.trendAnalysis?.tipJobId,
    results?.trendAnalysis?.tipsStatus,
    results?.trendAnalysis?.alignmentNotes,
    results?.trendAnalysis?.tipsSource,
    results?.trendAnalysis?.tipsFallbackNotes,
  ]);

  // --- Poll tip job (premium only) -------------------------------------------
  // Success → LLM/template notes from server.
  // Hard errors (400/401/403/404) stop immediately → tipsFallbackNotes.
  // Soft network errors keep polling until TIP_POLL_MAX_MS, then same fallback.
  useEffect(() => {
    if (!showPremiumContent || !tipJobId || tipsStatus === 'ready') return undefined;

    let cancelled = false;
    const startedAt = Date.now();

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/api/trend-insights/${tipJobId}`);
        if (cancelled) return;
        if (data?.tipsStatus === 'ready') {
          setTipsStatus('ready');
          setTipsSource(data.tipsSource || null);
          setLiveNotes(normalizeInsightNotes(data.alignmentNotes));
          return;
        }
      } catch (error) {
        const status = pollErrorStatus(error);
        // Owner mismatch / expired / missing — stop and use upload fallback tips.
        if (status === 403 || status === 404 || status === 400 || status === 401) {
          if (!cancelled) applyFallbackTips('template');
          return;
        }
        // Transient network errors: keep polling until cap.
      }
      if (Date.now() - startedAt >= TIP_POLL_MAX_MS) {
        if (!cancelled) applyFallbackTips('template');
        return;
      }
      timeoutId = setTimeout(poll, TIP_POLL_MS);
    };

    let timeoutId = setTimeout(poll, TIP_POLL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
    // tipsFallbackNotes captured via applyFallbackTips / closure from render.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- poll only for tip job lifecycle
  }, [showPremiumContent, tipJobId, tipsStatus, results?.trendAnalysis?.tipsFallbackNotes]);

  // Animate Genre fit View panel open/close (grid-rows expand trick).
  useEffect(() => {
    if (showGenreFitLearnMore) {
      setGenreFitPanelVisible(true);
      let raf2 = 0;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setGenreFitPanelEntered(true));
      });
      return () => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }

    setGenreFitPanelEntered(false);
    const t = setTimeout(() => setGenreFitPanelVisible(false), GENRE_FIT_PANEL_MS);
    return () => clearTimeout(t);
  }, [showGenreFitLearnMore]);

  // Animate Audience View panel open/close (snappier than Genre Fit).
  useEffect(() => {
    if (showAudienceLearnMore) {
      setAudiencePanelVisible(true);
      let raf2 = 0;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setAudiencePanelEntered(true));
      });
      return () => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }

    setAudiencePanelEntered(false);
    const t = setTimeout(() => setAudiencePanelVisible(false), AUDIENCE_PANEL_MS);
    return () => clearTimeout(t);
  }, [showAudienceLearnMore]);

  if (craftType !== 'music' || !results.trendAnalysis) {
    return null;
  }

  const selectedGenreLabel = formatSelectedGenreDisplay(results.trendAnalysis);
  const breakdown = results.trendAnalysis.trendFitBreakdown || null;
  const tipsPending = tipsStatus === 'pending' && Boolean(tipJobId);
  const demographics = results.trendAnalysis.demographics || {};
  const scoredLaneLabel = formatScoredLaneLabel(results.trendAnalysis);
  const hitScore = results.trendAnalysis.hitPotential?.score;
  const hitLabel = results.trendAnalysis.hitPotential?.label;

  return (
    <Card className="animate-slide-up glow-effect-blue hover-lift">
      <SectionHeader
        icon={<TrendingUp className="h-6 w-6 text-white" />}
        gradient="orange"
        title={<>Hit Potential &amp; Market Analysis</>}
        subtitle="Trends, fit, and commercial signals for your track."
        collapsed={sectionCollapsed}
        onToggleCollapse={() => {
          setSectionCollapsed((prev) => {
            if (!prev) {
              setShowGenreFitLearnMore(false);
              setFocusAxisId(null);
              setShowAudienceLearnMore(false);
            }
            return !prev;
          });
        }}
      />

      {!sectionCollapsed ? (
      <>
      {/* Block 1: Hit potential score */}
      <div className="mb-10 p-6 sm:p-8 bg-gradient-to-r from-apple-orange/15 to-apple-yellow/15 backdrop-blur-xl rounded-2xl border border-apple-orange/25 relative overflow-hidden">
        {!showPremiumContent && <LockOverlay title="Unlock hit potential" tone="orange" />}
        {showPremiumContent ? (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 mb-6">
              <div className="flex items-center space-x-3">
                <IconTile size="md" gradient="orange">
                  <span className="text-white font-bold text-lg">🎯</span>
                </IconTile>
                <h3 className="font-display text-xl sm:text-2xl font-semibold text-critic-text-primary">
                  Hit potential score
                </h3>
              </div>
              <div className="flex items-center justify-center space-x-3">
                <span
                  className={`text-3xl sm:text-4xl font-bold tabular-nums ${getHitPotentialColor(
                    hitScore ?? 0
                  )}`}
                >
                  {hitScore ?? '---'}
                </span>
                <Badge
                  tone={getHitPotentialBadgeTone(hitScore ?? 0)}
                  className="text-base sm:text-lg px-3 sm:px-4 py-2"
                >
                  {hitLabel || 'Locked'}
                </Badge>
              </div>
            </div>
            <div className="space-y-3">
              <div className="score-bar">
                <div
                  className="score-fill"
                  style={{ width: `${hitScore ?? 0}%` }}
                />
              </div>
              <div className="flex justify-between text-sm text-critic-text-muted font-normal">
                <span>Low potential</span>
                <span>High potential</span>
              </div>
            </div>
          </>
        ) : (
          <div className="opacity-30 blur-sm pointer-events-none">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 mb-6">
              <div className="flex items-center space-x-3">
                <IconTile size="md" gradient="orange">
                  <span className="text-white font-bold text-lg">🎯</span>
                </IconTile>
                <h3 className="font-display text-xl sm:text-2xl font-semibold text-critic-text-primary">
                  Hit potential score
                </h3>
              </div>
              <div className="flex items-center justify-center space-x-3">
                <span className="text-3xl sm:text-4xl font-bold tabular-nums text-critic-text-muted">--</span>
                <Badge tone="default" className="text-base sm:text-lg px-3 sm:px-4 py-2 opacity-50">
                  Locked
                </Badge>
              </div>
            </div>
            <div className="space-y-3">
              <div className="score-bar">
                <div className="score-fill bg-gray-600" style={{ width: '0%' }} />
              </div>
              <div className="flex justify-between text-sm text-critic-text-muted font-normal">
                <span>Low potential</span>
                <span>High potential</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Block 2: Trend alignment + Genre fit View */}
      <div className="mb-10 relative">
        {!showPremiumContent && (
          <LockOverlay title="Unlock market analysis" tone="blue" rounded />
        )}
        <h3 className="font-display text-xl sm:text-2xl font-semibold text-critic-text-primary mb-6 flex items-center space-x-3">
          <IconTile size="sm" gradient="insight">
            <span className="text-white font-bold text-sm">📊</span>
          </IconTile>
          <span>Trend alignment</span>
        </h3>
        <div className="grid grid-cols-2 gap-3 sm:gap-8 items-stretch">
          {showPremiumContent ? (
            <>
              <Card variant="inset" className="!px-4 !py-4 sm:!p-6 min-w-0 h-full flex flex-col">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <p className="text-sm font-medium text-critic-text-secondary shrink-0">Genre fit</p>
                  {!showGenreFitLearnMore ? (
                    <button
                      type="button"
                      onClick={() => openGenreFitLearnMore(null)}
                      className="shrink-0 relative inline-flex items-center justify-center min-h-11 min-w-11 -mr-1.5 sm:min-h-0 sm:min-w-0 sm:mr-0"
                      aria-label="View genre fit"
                    >
                      <span className="inline-flex items-center justify-center rounded-full border border-apple-blue/35 bg-apple-blue/10 px-2.5 py-1 text-[11px] font-medium text-apple-blue sm:text-xs pointer-events-none">
                        View
                      </span>
                    </button>
                  ) : null}
                </div>
                <div className="space-y-3 sm:space-y-4 flex-1">
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
                    <span className="text-critic-text-muted font-normal text-xs sm:text-sm">Genre</span>
                    <span className="text-critic-text-primary font-medium text-xs sm:text-base text-left sm:text-right break-words">
                      {scoredLaneLabel}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
                    <span className="text-critic-text-muted font-normal text-xs sm:text-sm">Trend alignment</span>
                    <span className="text-critic-text-primary font-bold text-xs sm:text-base">
                      {Math.round(results.trendAnalysis.trendAlignment * 100)}%
                    </span>
                  </div>
                </div>
              </Card>
              <Card variant="inset" className="!px-4 !py-4 sm:!p-6 min-w-0 h-full flex flex-col">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <p className="text-sm font-medium text-critic-text-secondary shrink-0">Audience</p>
                  {!showAudienceLearnMore ? (
                    <button
                      type="button"
                      onClick={() => setShowAudienceLearnMore(true)}
                      className="shrink-0 relative inline-flex items-center justify-center min-h-11 min-w-11 -mr-1.5 sm:min-h-0 sm:min-w-0 sm:mr-0"
                      aria-label="View audience"
                    >
                      <span className="inline-flex items-center justify-center rounded-full border border-apple-blue/35 bg-apple-blue/10 px-2.5 py-1 text-[11px] font-medium text-apple-blue sm:text-xs pointer-events-none">
                        View
                      </span>
                    </button>
                  ) : null}
                </div>
                <div className="space-y-3 sm:space-y-4 flex-1">
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
                    <span className="text-critic-text-muted font-normal text-xs sm:text-sm">Primary target</span>
                    <span className="text-critic-text-primary font-medium text-[11px] leading-snug sm:text-base text-left sm:text-right break-words">
                      {formatAgeGroup(demographics.primaryTarget)}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
                    <span className="text-critic-text-muted font-normal text-xs sm:text-sm">Primary market</span>
                    <span className="text-white font-semibold text-[11px] leading-snug sm:text-base break-words">
                      {formatMarket(demographics.primaryMarket)}
                    </span>
                  </div>
                </div>
              </Card>
            </>
          ) : (
            <>
              <Card variant="inset" className="!px-4 !py-4 sm:!p-6 min-w-0 h-full opacity-30 blur-sm">
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex justify-between gap-2">
                    <span className="text-critic-text-muted text-xs sm:text-sm">Genre</span>
                    <span className="text-critic-text-primary text-xs sm:text-sm">---</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-critic-text-muted text-xs sm:text-sm">Trend alignment</span>
                    <span className="text-critic-text-primary text-xs sm:text-sm">--%</span>
                  </div>
                </div>
              </Card>
              <Card variant="inset" className="!px-4 !py-4 sm:!p-6 min-w-0 h-full opacity-30 blur-sm">
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex justify-between gap-2">
                    <span className="text-critic-text-muted text-xs sm:text-sm">Primary target</span>
                    <span className="text-critic-text-primary text-xs sm:text-sm">---</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-critic-text-muted text-xs sm:text-sm">Primary market</span>
                    <span className="text-critic-text-primary text-xs sm:text-sm">---</span>
                  </div>
                </div>
              </Card>
            </>
          )}
        </div>

        {/* Full-width panels below the summary cards (same shade as each other). */}
        {showPremiumContent && audiencePanelVisible ? (
          <div
            className={`mt-4 grid transition-[grid-template-rows,opacity,transform] duration-200 ease-critic-out ${
              audiencePanelEntered
                ? 'grid-rows-[1fr] opacity-100 translate-y-0'
                : 'grid-rows-[0fr] opacity-0 -translate-y-1'
            }`}
          >
            <div className="min-h-0 overflow-hidden">
              <AudienceLearnMore
                demographics={demographics}
                audienceLabel={scoredLaneLabel}
                panelReady={audiencePanelEntered}
                onClose={() => setShowAudienceLearnMore(false)}
              />
            </div>
          </div>
        ) : null}

        {/* Tip taps can also open this panel with focusAxisId set. */}
        {showPremiumContent && genreFitPanelVisible ? (
          <div
            className={`mt-4 grid transition-[grid-template-rows,opacity,transform] duration-500 ease-critic-out ${
              genreFitPanelEntered
                ? 'grid-rows-[1fr] opacity-100 translate-y-0'
                : 'grid-rows-[0fr] opacity-0 -translate-y-1'
            }`}
          >
            <div className="min-h-0 overflow-hidden">
              <GenreFitLearnMore
                breakdown={breakdown}
                selectedGenreLabel={selectedGenreLabel}
                focusAxisId={focusAxisId}
                onFocusAxisChange={setFocusAxisId}
                panelReady={genreFitPanelEntered}
                onClose={closeGenreFitLearnMore}
              />
            </div>
          </div>
        ) : null}
      </div>

      {/* Block 3: Trend insights (left) + Trending features (right) */}
      {(() => {
        const notes = liveNotes;
        const features = (results.trendAnalysis.trendFeatures || []).slice(0, 5);
        const hasNotes = notes.length > 0;
        const hasFeatures = features.length > 0;
        // Keep the insights column visible while skeletons are showing.
        const showInsightColumn = hasNotes || tipsPending || !showPremiumContent;
        if (!showInsightColumn && !hasFeatures) return null;

        return (
          <div className="relative">
            {!showPremiumContent && (
              <LockOverlay title="Unlock trend details" tone="green" rounded />
            )}
            <Card
              variant="inset"
              className={`p-4 sm:p-6 overflow-hidden ${!showPremiumContent ? 'opacity-30 blur-sm' : ''}`}
            >
              <div className="flex flex-col sm:flex-row gap-6 sm:gap-0">
                {/* Left: skeletons → tip cards with axis badges; tap opens View */}
                <div className="min-w-0 flex-1 sm:pr-6">
                  <h3 className="font-display text-xl sm:text-2xl font-semibold text-critic-text-primary mb-4 sm:mb-5 flex items-center space-x-3">
                    <IconTile size="sm" gradient="green">
                      <span className="text-white font-bold text-sm">💡</span>
                    </IconTile>
                    <span>Trend insights</span>
                  </h3>
                  <div className="space-y-3" data-tips-source={tipsSource || undefined}>
                    {showPremiumContent ? (
                      tipsPending ? (
                        /* Pending: three pulse cards matching tip height (no template flash) */
                        Array.from({ length: 3 }).map((_, index) => (
                          <div
                            key={`tip-skel-${index}`}
                            className="p-3.5 sm:p-4 rounded-xl border border-apple-green/15 bg-apple-green/5 animate-pulse"
                            aria-hidden={index > 0}
                            {...(index === 0
                              ? { role: 'status', 'aria-label': 'Generating trend insights' }
                              : {})}
                          >
                            <div className="h-3 w-14 rounded bg-white/10 mb-3" />
                            <div className="h-3 w-full rounded bg-white/10 mb-2" />
                            <div className="h-3 w-5/6 rounded bg-white/10" />
                          </div>
                        ))
                      ) : hasNotes ? (
                        /* Ready: badge + text; move tips jump to View on that axis */
                        notes.map((note, index) => {
                          const isMove = note.kind === 'move' && note.axisId;
                          const isAffirm = note.kind === 'affirm';
                          const badgeLabel = isAffirm
                            ? 'Match'
                            : getAxisFitLabel(note.axisId) || 'Tip';
                          const canJump = isMove || isAffirm;
                          const tipClassName =
                            'w-full text-left p-3.5 sm:p-4 bg-gradient-to-r from-apple-green/10 to-apple-teal/10 rounded-xl border border-apple-green/20 animate-slide-up transition-colors ' +
                            (canJump
                              ? 'hover:border-apple-green/40 hover:from-apple-green/15 hover:to-apple-teal/15 cursor-pointer'
                              : '');

                          const body = (
                            <>
                              <span className="inline-flex items-center rounded-2xl border border-apple-green/30 bg-apple-green/10 px-2 py-0.5 text-[11px] font-medium text-apple-green mb-2">
                                {badgeLabel}
                              </span>
                              <p className="text-critic-text-secondary text-sm sm:text-[0.95rem] leading-relaxed font-normal">
                                {note.text}
                              </p>
                            </>
                          );

                          if (canJump) {
                            const ariaLabel = isAffirm
                              ? 'Open genre fit details'
                              : `Open ${badgeLabel} genre fit details`;
                            return (
                              <button
                                key={note.axisId || note.kind || index}
                                type="button"
                                data-trend-move-tip={isMove ? 'true' : undefined}
                                className={tipClassName}
                                style={{ animationDelay: `${index * 80}ms` }}
                                aria-label={ariaLabel}
                                onClick={() =>
                                  openGenreFitLearnMore(isMove ? note.axisId : null)
                                }
                              >
                                {body}
                              </button>
                            );
                          }

                          return (
                            <div
                              key={note.axisId || note.kind || index}
                              className={tipClassName}
                              style={{ animationDelay: `${index * 80}ms` }}
                            >
                              {body}
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-sm text-critic-text-muted">No trend insights for this track.</p>
                      )
                    ) : (
                      <div className="p-3.5 sm:p-4 bg-gradient-to-r from-apple-green/20 to-apple-teal/20 rounded-xl border border-apple-green/30">
                        <p className="text-critic-text-secondary text-sm leading-relaxed">
                          Sign up to see detailed trend insights...
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right column: affinity-ranked popular_features chips (3–5) */}
                {/* Wider than the old w-44/52 so “Trending features” stays one line */}
                <div className="sm:w-56 md:w-64 shrink-0 sm:border-l sm:border-white/[0.08] sm:pl-6">
                  <h3 className="font-display text-lg sm:text-xl font-semibold text-critic-text-primary mb-4 sm:mb-5 flex items-center space-x-3 whitespace-nowrap">
                    <IconTile size="sm" gradient="purple">
                      <span className="text-white font-bold text-sm">🔥</span>
                    </IconTile>
                    <span>Trending features</span>
                  </h3>
                  {/* Brick wrap: chips sit side-by-side and wrap to the next line */}
                  <div className="flex flex-row flex-wrap gap-1.5 content-start">
                    {showPremiumContent
                      ? hasFeatures
                        ? features.map((feature, index) => (
                            <Chip
                              key={feature}
                              className="animate-slide-up shrink-0 !px-2.5 !py-1.5 !text-[11px] !rounded-xl font-medium"
                              style={{ animationDelay: `${index * 50}ms` }}
                            >
                              {feature
                                .replace(/_/g, ' ')
                                .replace(/\b\w/g, (c) => c.toUpperCase())}
                            </Chip>
                          ))
                        : (
                            <p className="text-sm text-critic-text-muted">No features detected.</p>
                          )
                      : Array.from({ length: 4 }).map((_, index) => (
                          <Chip
                            key={index}
                            locked
                            className="shrink-0 !px-2.5 !py-1.5 !text-[11px] !rounded-xl"
                          >
                            ---
                          </Chip>
                        ))}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        );
      })()}
      </>
      ) : null}
    </Card>
  );
}
